import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import Portal from 'components/Portal/Portal'
import { Modal } from 'components/Modal/Modal'
import DateRow from 'components/DateRow/DateRow'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { ContextButton } from '@tecuity/granite-ui'
import * as api from '../../../requests'
import format from 'date-fns/format'
import './EditFilingModal.scss'
import { useLocation } from 'react-router'
import { setSuccessToast } from '../../../actions'

const EditFilingModal = ({
  editModalOpen,
  setEditModalOpen,
  label,
  filing,
  template,
  setHistory,
  module,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [optionsMap, setOptionsMap] = useState({})
  const [editItem, setEditItem] = useState({})
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { appUserRights } = useSelector((state) => state.auth, shallowEqual)
  const closeEditModal = () => {
    setEditModalOpen(false)
  }

  useEffect(() => {
    if (filing && editableFields.length > 0) {
      const item = { FilingNumber: filing.FilingNumber }
      editableFields.forEach((f) => {
        switch (f.editType) {
          case 'dropdown':
            item[f.id] = optionsMap[f.id]
              ? optionsMap[f.id].find((opt) => {
                  let typeName = filing[f.id]
                  if (filing.AnnualReportFilingYear && filing.AnnualReportFilingYear > 0) {
                    typeName = typeName.replace(filing.AnnualReportFilingYear.toString(), '').trim()
                  }
                  return opt.label === typeName
                }).value
              : filing[f.id]
            break
          case 'number':
            item[f.id] = !isNaN(Number(filing[f.id])) ? Number(filing[f.id]) : null
            break
          default:
            item[f.id] = filing[f.id]
        }
      })
      setEditItem(item)
    }
  }, [filing, editableFields, optionsMap])

  const editableFields = useMemo(() => {
    return template.filter((f) => f.isEditable)
  }, [template])

  useEffect(() => {
    if (editModalOpen)
      editableFields.forEach((f) => {
        switch (f.editType) {
          case 'dropdown':
            // get options for dropdowns
            api.GET(
              f.apiCall,
              (data) => {
                setOptionsMap((om) => ({
                  ...om,
                  [f.id]: data.map((t) => ({
                    value: t.Id,
                    label: t.Name,
                  })),
                }))
              },
              (err) => console.error(err)
            )
            break
          default:
        }
      })
  }, [editableFields, editModalOpen])

  const getFieldByType = (f) => {
    switch (f.editType) {
      case 'dropdown':
        return (
          <SelectBox
            key={f.id}
            options={optionsMap[f.id] || []}
            value={editItem[f.id] || ''}
            label={f.label}
            onChange={(e) => {
              setEditItem({ ...editItem, [f.id]: e })
            }}
          />
        )
      case 'date':
        return (
          <div key={f.id}>
            <div className="property-label">{`${f.label}\n`}</div>
            <DateRow
              rowLabel={f.label}
              date={
                editItem[f.id] && editItem[f.id].length >= 10
                  ? format(new Date(editItem[f.id]), 'MM/dd/yyyy')
                  : editItem[f.id] || ''
              }
              setDate={(e) => {
                if (!isNaN(e.replaceAll('/', ''))) {
                  setEditItem({ ...editItem, [f.id]: e })
                }
              }}
            />
          </div>
        )
      case 'number':
        return (
          <TextInput
            customCss={`width: 20%; min-width: 150px;`}
            key={f.id}
            label={f.label}
            value={editItem[f.id] || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [f.id]: +e.target.value })
            }}
            type="number"
            min="0"
            step="1"
            pattern="\d+"
          />
        )
      case 'text':
        return (
          <TextInput
            key={f.id}
            label={f.label}
            value={editItem[f.id] || ''}
            onChange={(e) => {
              setEditItem({ ...editItem, [f.id]: e.target.value })
            }}
          />
        )
      default:
        console.warn(`Editable type: '${f.editType}' not yet supported`)
        return null
    }
  }

  const saveAndClose = () => {
    if (module === 'business' && appUserRights.includes('EditBusinessFiling')) {
      setErrorMessage('')
      setLoading(true)
      api.EDIT_BUSINESS_FILING(
        editItem,
        (res) => {
          setLoading(false)
          dispatch(setSuccessToast(`Updated filing #${filing.FilingNumber}.`))
          closeEditModal()
          let path = location.pathname.split('/')
          api.FETCH_BUSINESS_RECORD_DETAIL_HISTORY(
            path[path.length - 1],
            (recordDetailHistory) => {
              setHistory(recordDetailHistory)
            },
            (e) => {
              console.error(e)
            }
          )
        },
        (err) => {
          setErrorMessage(err)
          setLoading(false)
        }
      )
    }

    if (module === 'trademark' && appUserRights.includes('EditTrademarkFiling')) {
      setErrorMessage('')
      setLoading(true)
      api.EDIT_TRADEMARK_FILING(
        editItem,
        () => {
          setLoading(false)
          dispatch(setSuccessToast(`Updated filing #${filing.FilingNumber}.`))
          closeEditModal()
          setHistory() // the refresh will be handled in TrademarkRecordDetails.js
        },
        (err) => {
          setErrorMessage(err)
          setLoading(false)
        }
      )
    }
  }

  return (
    <Portal isOpened={editModalOpen}>
      <Modal
        title={`${label} ${filing.FilingNumber}`}
        cancelButton
        autoHeight
        modalSize={'medium'}
        closeModal={closeEditModal}
      >
        <div className="edit-filing-modal">
          {editableFields.map((f) => {
            return getFieldByType(f)
          })}
          {errorMessage ? <div className="error-message">{errorMessage}</div> : ''}
          <div className="context-bar">
            <ContextButton variant="secondary" onClick={closeEditModal} disabled={loading}>
              Cancel
            </ContextButton>
            <ContextButton onClick={saveAndClose} variant="primary" disabled={loading}>
              Save
            </ContextButton>
          </div>
        </div>
      </Modal>
    </Portal>
  )
}

export default EditFilingModal
